import { Paper, Chip, Tooltip, Modal, Box, Button, Typography, TextField, IconButton } from '@mui/material';
import { FC } from 'react';
import PhoneInTalkOutlinedIcon from '@mui/icons-material/PhoneInTalkOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PhoneDisabledIcon from '@mui/icons-material/PhoneDisabled';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import { SCUILoadingLinear } from '../../../../atomic/atoms';
import { CCP } from './customerCardPaper.classes';
import { calculateStrokeDashoffset, formatCountdown, useCustomerCardPaper } from './useCustomerCardPaper';

interface IProps {
    datas?: any;
    refresh?: any;
    /** Store customer screen: hide phone update and remove-from-membership actions */
    hidePhoneManagement?: boolean;
}

const ignoreBackdropClose = (reason: string, onClose: () => void) => {
    if (reason === 'backdropClick') return;
    onClose();
};

export const CustomerCardPaper: FC<IProps> = ({ datas, refresh, hidePhoneManagement = false }) => {
    const {
        t,
        otpRefs,
        removePhoneOtpRefs,
        open,
        otpOpen,
        removePhoneOtpOpen,
        phoneNumber,
        otp,
        removePhoneOtp,
        timeLeft,
        removePhoneTimeLeft,
        removePhoneTimeTotal,
        timeExpired,
        removePhoneTimeExpired,
        phoneNumberError,
        loading,
        removePhoneLoading,
        removePhoneOtpLoading,
        copiedField,
        removePhoneDisplayNumber,
        isRemovePhoneOtpCompleteDisabled,
        handleCopy,
        handlePhoneNumberChange,
        handlePhoneNumberKeyDown,
        handlePhoneNumberSelect,
        handleOpen,
        handleClose,
        handleOtpClose,
        handleRemovePhoneOtpClose,
        handleUpdate,
        handleOtpChange,
        handleResendCode,
        handleRemovePhoneNumber,
        handleRemovePhoneOtpChange,
        handleRemovePhoneOtpComplete,
        handleRemovePhoneResendCode,
    } = useCustomerCardPaper({ datas, refresh });

    const customerFullName = `${datas?.customer.firstName ?? ''} ${datas?.customer.lastName ?? ''}`.trim();
    const phoneDisplay = datas.customer.phoneNumber
        ? `${datas.customer.phoneAreaCode} ${datas.customer.phoneNumber}`
        : '-';
    const isActive = datas.customer.status === 1;

    return (
        <Paper
            className={CCP.card}
            elevation={0}
            sx={{
                width: '100%',
                minHeight: '7rem',
                justifyContent: 'center',
                px: 4,
                py: 2.5,
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
        >
            <Box className={CCP.header} sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <Box className={CCP.identityBlock} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Chip
                        className={CCP.fieldChipStatus}
                        label={isActive ? t('general.active') : t('general.passive')}
                        color={isActive ? 'success' : 'error'}
                        size="medium"
                        sx={{ fontWeight: 700, letterSpacing: 0.3 }}
                    />
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>
                            {customerFullName}
                        </Typography>
                        <Box className={CCP.customerIdRow} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                                {`${t('customerInformation.customerNo')}: ${datas.customer.customerId}`}
                            </Typography>
                            <Tooltip title={copiedField === 'customerId' ? 'Copied!' : t('general.copyToClipboard')}>
                                <IconButton
                                    className={CCP.inputIconButtonCopyCustomerId}
                                    size="small"
                                    onClick={() => handleCopy(String(datas.customer.customerId), 'customerId')}
                                    sx={{
                                        p: 0.25,
                                        color: copiedField === 'customerId' ? 'success.main' : 'text.disabled',
                                    }}
                                >
                                    <ContentCopyIcon sx={{ fontSize: 13 }} />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                </Box>

                <Box className={CCP.divider} sx={{ width: '1px', height: 36, bgcolor: 'divider', flexShrink: 0 }} />

                <Box className={CCP.phoneRow} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PhoneInTalkOutlinedIcon sx={{ color: '#0D6EFD', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={500}>
                        {phoneDisplay}
                    </Typography>
                    <Tooltip title={copiedField === 'phone' ? 'Copied!' : t('general.copyToClipboard')}>
                        <IconButton
                            className={CCP.inputIconButtonCopyPhone}
                            size="small"
                            onClick={() => handleCopy(phoneDisplay === '-' ? '' : phoneDisplay, 'phone')}
                            sx={{ p: 0.25, color: copiedField === 'phone' ? 'success.main' : 'text.disabled' }}
                        >
                            <ContentCopyIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                    </Tooltip>
                    {!hidePhoneManagement && datas.customer?.backofficeCustomerEditPhoneNumberShowStatusTemplate && (
                        <Tooltip title={t('customerInformation.phoneNumberUpdate')}>
                            <IconButton
                                className={CCP.inputIconButtonEditPhone}
                                onClick={handleOpen}
                                sx={{
                                    color: '#0D6EFD',
                                    p: 0.75,
                                    borderRadius: 1,
                                    '&:hover': { backgroundColor: 'rgba(13,110,253,0.08)' },
                                }}
                            >
                                <EditNoteIcon sx={{ fontSize: 22 }} />
                            </IconButton>
                        </Tooltip>
                    )}
                    {!hidePhoneManagement && (
                        <Tooltip title={t('customerInformation.removePhoneFromOldMembership')}>
                            <span>
                                <IconButton
                                    className={CCP.inputIconButtonRemovePhone}
                                    size="small"
                                    disabled={!datas.customer.phoneNumber || removePhoneLoading}
                                    onClick={handleRemovePhoneNumber}
                                    sx={{ color: '#D32F2F', ml: 0.5 }}
                                >
                                    <PhoneDisabledIcon fontSize="medium" />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </Box>

                <Box className={CCP.divider} sx={{ width: '1px', height: 36, bgcolor: 'divider', flexShrink: 0 }} />

                <Box className={CCP.emailRow} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <EmailOutlinedIcon sx={{ color: '#0D6EFD', fontSize: 20 }} />
                    <Typography variant="body2" fontWeight={500}>
                        {datas.customer.email}
                    </Typography>
                    <Tooltip title={copiedField === 'email' ? 'Copied!' : t('general.copyToClipboard')}>
                        <IconButton
                            className={CCP.inputIconButtonCopyEmail}
                            size="small"
                            onClick={() => handleCopy(datas.customer.email ?? '', 'email')}
                            sx={{ p: 0.25, color: copiedField === 'email' ? 'success.main' : 'text.disabled' }}
                        >
                            <ContentCopyIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            <Modal
                className={CCP.modalPhone}
                open={open}
                onClose={(_, reason) => ignoreBackdropClose(reason, handleClose)}
                disableEscapeKeyDown
            >
                <Box
                    className={CCP.modalPhoneContent}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 400,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 4,
                    }}
                >
                    <Typography className={CCP.modalPhoneTitle} variant="h6" fontWeight={600} mb={1}>
                        {t('customerInformation.newPhoneNumber')}
                    </Typography>
                    <TextField
                        className={CCP.inputTextfieldPhone}
                        id="phone-number"
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        onKeyDown={handlePhoneNumberKeyDown}
                        onSelect={handlePhoneNumberSelect}
                        fullWidth
                        margin="dense"
                        slotProps={{
                            htmlInput: {
                                maxLength: 10,
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                style: { caretColor: phoneNumber.length === 0 ? 'transparent' : 'auto' },
                            },
                        }}
                        error={phoneNumberError}
                        helperText={phoneNumberError ? t('customerInformation.inputPhoneNumberException') : ''}
                        sx={{
                            '& .MuiInputBase-input': {
                                color: phoneNumberError ? '#D32F2F' : 'inherit',
                            },
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: phoneNumberError ? '#D32F2F' : 'inherit' },
                                '&:hover fieldset': { borderColor: phoneNumberError ? '#D32F2F' : 'inherit' },
                                '&.Mui-focused fieldset': { borderColor: phoneNumberError ? '#D32F2F' : 'inherit' },
                            },
                        }}
                    />
                    <Box className={CCP.modalPhoneLoading} sx={{ mt: 2 }}>
                        <SCUILoadingLinear display={loading} />
                    </Box>
                    <Box
                        className={CCP.modalPhoneActions}
                        sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mt: 2 }}
                    >
                        <Button
                            className={CCP.inputButtonPhoneUpdate}
                            variant="contained"
                            onClick={handleUpdate}
                            sx={{ textTransform: 'none', borderRadius: 1.5 }}
                        >
                            {t('general.update')}
                        </Button>
                        <Button
                            className={CCP.inputButtonPhoneCancel}
                            variant="contained"
                            color="error"
                            onClick={handleClose}
                            sx={{ textTransform: 'none', borderRadius: 1.5 }}
                        >
                            {t('general.cancel')}
                        </Button>
                    </Box>
                </Box>
            </Modal>

            <Modal
                className={CCP.modalOtp}
                open={otpOpen}
                onClose={(_, reason) => ignoreBackdropClose(reason, handleOtpClose)}
                disableEscapeKeyDown
            >
                <Box
                    className={CCP.modalOtpContent}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 420,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 4,
                        textAlign: 'center',
                    }}
                >
                    <IconButton
                        className={CCP.inputIconButtonOtpClose}
                        onClick={handleOtpClose}
                        size="small"
                        sx={{ position: 'absolute', right: 10, top: 10, color: 'text.secondary' }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>

                    <Box
                        className={CCP.otpCountdownRing}
                        sx={{ position: 'relative', width: 80, height: 80, mx: 'auto', mb: 2 }}
                    >
                        <svg width="80" height="80">
                            <circle cx="40" cy="40" r="35" stroke="#e0e0e0" strokeWidth="5" fill="none" />
                            <circle
                                cx="40"
                                cy="40"
                                r="35"
                                stroke={timeLeft <= 10 ? '#D32F2F' : '#4E8BFC'}
                                strokeWidth="5"
                                fill="none"
                                strokeDasharray="219.911"
                                strokeDashoffset={calculateStrokeDashoffset(timeLeft, 180)}
                                transform="rotate(-90 40 40)"
                            />
                        </svg>
                        <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: timeLeft <= 10 ? '#D32F2F' : '#4E8BFC',
                                fontSize: 13,
                            }}
                        >
                            {formatCountdown(timeLeft)}
                        </Typography>
                    </Box>

                    <Typography className={CCP.otpTitle} variant="h6" fontWeight={700}>
                        {t('general.phoneVerificationCode')}
                    </Typography>
                    <Typography className={CCP.otpSubtitle} variant="body2" color="text.secondary" mt={1}>
                        {t('general.registeredInSystem')} <b>{phoneNumber}</b> {t('general.enterVerificationCode')}
                    </Typography>

                    <Box className={CCP.modalOtpLoading} sx={{ mt: 2 }}>
                        <SCUILoadingLinear display={loading} />
                    </Box>

                    <Box className={CCP.otpInputsRow} sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                        {otp.map((digit, index) => (
                            <TextField
                                key={index}
                                className={CCP.inputTextfieldOtpDigit}
                                value={digit}
                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                slotProps={{
                                    htmlInput: {
                                        ref: (el: HTMLInputElement | null) => (otpRefs.current[index] = el),
                                        maxLength: 1,
                                        style: {
                                            textAlign: 'center',
                                            fontSize: '20px',
                                            fontWeight: 700,
                                            color: timeExpired ? '#D32F2F' : 'inherit',
                                        },
                                    },
                                }}
                                sx={{
                                    width: 44,
                                    '& .MuiInputBase-input': { color: timeExpired ? '#D32F2F' : 'inherit' },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: timeExpired ? '#D32F2F' : 'inherit' },
                                        '&:hover fieldset': { borderColor: timeExpired ? '#D32F2F' : 'inherit' },
                                        '&.Mui-focused fieldset': {
                                            borderColor: timeExpired ? '#D32F2F' : '#4E8BFC',
                                        },
                                    },
                                }}
                            />
                        ))}
                    </Box>

                    {timeExpired && (
                        <Box className={CCP.otpResendSection} mt={2}>
                            <Typography variant="body2" color="error" mb={1.5}>
                                {t('Süren doldu; tekrar doğrulama kodu almalısın.')}
                            </Typography>
                            <Button
                                className={CCP.inputButtonResendCode}
                                variant="contained"
                                onClick={handleResendCode}
                                startIcon={<SyncIcon />}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 1.5,
                                    px: 4,
                                }}
                            >
                                {t('general.resendCode')}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Modal>

            <Modal
                className={CCP.modalRemovePhoneOtp}
                open={removePhoneOtpOpen}
                onClose={(_, reason) => ignoreBackdropClose(reason, handleRemovePhoneOtpClose)}
                disableEscapeKeyDown
            >
                <Box
                    className={CCP.modalRemovePhoneOtpContent}
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 420,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 4,
                        textAlign: 'center',
                    }}
                >
                    <IconButton
                        className={CCP.inputIconButtonRemovePhoneOtpClose}
                        onClick={handleRemovePhoneOtpClose}
                        size="small"
                        sx={{ position: 'absolute', right: 10, top: 10, color: 'text.secondary' }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>

                    <Box
                        className={CCP.removePhoneOtpCountdownRing}
                        sx={{ position: 'relative', width: 80, height: 80, mx: 'auto', mb: 2 }}
                    >
                        <svg width="80" height="80">
                            <circle cx="40" cy="40" r="35" stroke="#e0e0e0" strokeWidth="5" fill="none" />
                            <circle
                                cx="40"
                                cy="40"
                                r="35"
                                stroke={removePhoneTimeLeft <= 10 ? '#D32F2F' : '#4E8BFC'}
                                strokeWidth="5"
                                fill="none"
                                strokeDasharray="219.911"
                                strokeDashoffset={calculateStrokeDashoffset(
                                    removePhoneTimeLeft,
                                    removePhoneTimeTotal
                                )}
                                transform="rotate(-90 40 40)"
                            />
                        </svg>
                        <Typography
                            variant="caption"
                            fontWeight={700}
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                color: removePhoneTimeLeft <= 10 ? '#D32F2F' : '#4E8BFC',
                                fontSize: 13,
                            }}
                        >
                            {formatCountdown(removePhoneTimeLeft)}
                        </Typography>
                    </Box>

                    <Typography className={CCP.removePhoneOtpTitle} variant="h6" fontWeight={700}>
                        {t('general.phoneVerificationCode')}
                    </Typography>
                    <Typography className={CCP.removePhoneOtpSubtitle} variant="body2" color="text.secondary" mt={1}>
                        {t('general.registeredInSystem')} <b>{removePhoneDisplayNumber}</b>{' '}
                        {t('general.enterVerificationCode')}
                    </Typography>

                    <Box className={CCP.modalRemovePhoneOtpLoading} sx={{ mt: 2 }}>
                        <SCUILoadingLinear display={removePhoneOtpLoading} />
                    </Box>

                    <Box
                        className={CCP.removePhoneOtpInputsRow}
                        sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}
                    >
                        {removePhoneOtp.map((digit, index) => (
                            <TextField
                                key={index}
                                className={CCP.inputTextfieldRemovePhoneOtpDigit}
                                value={digit}
                                onChange={(e) => handleRemovePhoneOtpChange(index, e.target.value)}
                                slotProps={{
                                    htmlInput: {
                                        ref: (el: HTMLInputElement | null) => (removePhoneOtpRefs.current[index] = el),
                                        maxLength: 1,
                                        style: {
                                            textAlign: 'center',
                                            fontSize: '20px',
                                            fontWeight: 700,
                                            color: removePhoneTimeExpired ? '#D32F2F' : 'inherit',
                                        },
                                    },
                                }}
                                sx={{
                                    width: 44,
                                    '& .MuiInputBase-input': {
                                        color: removePhoneTimeExpired ? '#D32F2F' : 'inherit',
                                    },
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: removePhoneTimeExpired ? '#D32F2F' : 'inherit' },
                                        '&:hover fieldset': {
                                            borderColor: removePhoneTimeExpired ? '#D32F2F' : 'inherit',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: removePhoneTimeExpired ? '#D32F2F' : '#4E8BFC',
                                        },
                                    },
                                }}
                            />
                        ))}
                    </Box>

                    {removePhoneTimeExpired && (
                        <Box className={CCP.removePhoneOtpResendSection} mt={2}>
                            <Typography variant="body2" color="error" mb={1.5}>
                                {t('customerInformation.removePhoneOtpTimeExpired')}
                            </Typography>
                            <Button
                                className={CCP.inputButtonRemovePhoneResendCode}
                                variant="contained"
                                onClick={handleRemovePhoneResendCode}
                                startIcon={<SyncIcon />}
                                sx={{
                                    textTransform: 'none',
                                    borderRadius: 1.5,
                                    px: 4,
                                }}
                            >
                                {t('general.resendCode')}
                            </Button>
                        </Box>
                    )}

                    <Box
                        className={CCP.removePhoneOtpActions}
                        sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}
                    >
                        <Button
                            className={CCP.inputButtonRemovePhoneOtpComplete}
                            variant="contained"
                            onClick={handleRemovePhoneOtpComplete}
                            disabled={isRemovePhoneOtpCompleteDisabled}
                            sx={{ textTransform: 'none', borderRadius: 1.5, px: 4 }}
                        >
                            {t('general.complete')}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </Paper>
    );
};
